import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { get } from "../../lib/api";
import { dateOnly, kwh, percent } from "../../lib/format";
import { ancestry } from "../../lib/holders";
import { useSession } from "../../lib/session";
import type { HolderListItem, IdentityDetail } from "../../lib/types";
import { HOLDERS_KEY, useHolders } from "../holders/HolderSelect";
import { Button } from "../ui/Button";
import { ErrorNotice } from "../ui/Feedback";
import { Modal } from "../ui/Modal";
import { Pill } from "../ui/Status";
import { Datum, MicroLabel, SectionTitle } from "../ui/Text";
import { DeleteIdentity } from "./EditIdentity";
import { QuotaBar } from "./QuotaBar";

/**
 * One identity at a glance, over the list it was clicked from: the card, the
 * holder it belongs to with that holder's own record, and the pool it draws on.
 * Read-only — changing anything goes through Edit, which is a modal of its own.
 * Removal sits at the foot of the body, under the facts that decide it; the
 * standalone identity page is being retired, so nothing here links to it.
 *
 * The holder's record comes from the holder list every holder picker already
 * caches, not a second request per card: it carries the type, reference, status,
 * parent and card count this panel shows.
 */

/** Dates here are UTC for the reason the detail page gives in `POLICY_ZONE_NOTE`. */
const ZONE = "UTC";

export function IdentityView({
  identityId,
  open,
  onOpenChange,
  onEdit,
}: {
  readonly identityId: string;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onEdit: (identity: IdentityDetail) => void;
}) {
  const { can } = useSession();
  const queryClient = useQueryClient();
  const detail = useQuery({
    queryKey: ["identity", identityId] as const,
    queryFn: () => get<IdentityDetail>(`/identities/${encodeURIComponent(identityId)}`),
    enabled: open && identityId !== "",
  });
  const row = detail.data;

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      eyebrow={row === undefined ? "Identity" : `${tokenLabel(row)} · ${row.idTag}`}
      title={row?.label ?? "Identity"}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          {row !== undefined && can("identities.update") ? (
            <Button variant="primary" onClick={() => onEdit(row)}>
              Edit
            </Button>
          ) : null}
        </>
      }
    >
      {detail.isError ? (
        <ErrorNotice error={detail.error} onRetry={() => void detail.refetch()} />
      ) : row === undefined ? (
        <MicroLabel>Loading</MicroLabel>
      ) : (
        <>
          <IdentityFacts row={row} />
          {can("identities.delete") ? (
            <div className="border-t border-rule pt-4">
              <DeleteIdentity
                identity={row}
                onDeleted={() => {
                  void queryClient.invalidateQueries({ queryKey: ["identities"] });
                  void queryClient.invalidateQueries({ queryKey: HOLDERS_KEY });
                  queryClient.removeQueries({ queryKey: ["identity", row.id] });
                  onOpenChange(false);
                }}
              />
            </div>
          ) : null}
        </>
      )}
    </Modal>
  );
}

function IdentityFacts({ row }: { readonly row: IdentityDetail }) {
  const holders = useHolders();
  const holder = holders.data?.find((candidate) => candidate.id === row.holder.id);
  const period = row.currentPeriod;

  return (
    <>
      <section className="flex flex-col gap-3">
        <SectionTitle>Card</SectionTitle>
        <dl className="m-0 grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3">
          <Datum label="ID tag" mono>
            {row.idTag}
          </Datum>
          <Datum label="Token">{row.tokenType === "vin" ? "Vehicle VIN" : "RFID card"}</Datum>
          <Datum label="External ref" mono={row.externalRef !== null}>
            {row.externalRef ?? "—"}
          </Datum>
          <Datum label="Status">
            <Pill tone={row.status === "Accepted" ? "available" : "faulted"}>{row.status}</Pill>
          </Datum>
          <Datum label="Expires">
            {row.expiryDate === null ? "No expiry" : dateOnly(row.expiryDate, ZONE)}
          </Datum>
          <Datum label="Registered">{dateOnly(row.createdAt, ZONE)}</Datum>
          <Datum label="Sessions recorded">
            {row.sessionCount === 0 ? (
              0
            ) : (
              <Link
                to={`/sessions?identityId=${encodeURIComponent(row.id)}`}
                className="text-signal underline decoration-1 underline-offset-4 hover:no-underline"
              >
                {row.sessionCount}
              </Link>
            )}
          </Datum>
        </dl>
      </section>

      <section className="flex flex-col gap-3">
        <SectionTitle>Holder</SectionTitle>
        <dl className="m-0 grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3">
          <Datum label="Name">
            <HolderLink id={row.holder.id} name={row.holder.name} />
          </Datum>
          <Datum label="Type">{row.holder.type === "person" ? "Person" : "Group"}</Datum>
          {holder === undefined ? (
            <Datum label="Record">{holders.isError ? "Could not be loaded" : "Loading…"}</Datum>
          ) : (
            <HolderRecord holder={holder} all={holders.data ?? []} />
          )}
        </dl>
      </section>

      <section className="flex flex-col gap-3">
        <SectionTitle>Allowance</SectionTitle>
        {row.allowance === null ? (
          <p className="m-0 max-w-prose text-body text-ink-muted">
            No allowance governs {row.holder.name} or any group above it. Energy is still recorded
            against this card.
          </p>
        ) : (
          <>
            {period === null ? null : (
              <QuotaBar
                committedWh={period.committedWh}
                inFlightWh={period.inFlightWh}
                quotaWh={period.quotaWh}
                thresholds={row.allowance.policy.warnThresholds}
              />
            )}
            <dl className="m-0 grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3">
              <Datum label="Policy">{row.allowance.policy.name}</Datum>
              <Datum label="Pool">
                <HolderLink id={row.allowance.holder.id} name={row.allowance.holder.name} />
              </Datum>
              <Datum label="Quota">{kwh(row.allowance.policy.quotaWh)} kWh, shared</Datum>
              {period === null ? null : (
                <>
                  <Datum label="Period">{period.label}</Datum>
                  <Datum label="Committed">{kwh(period.committedWh)} kWh</Datum>
                  {/* Spec 6.3: a live estimate, kept apart from the billable figure. */}
                  <Datum label="In flight">{kwh(period.inFlightWh)} kWh, estimate</Datum>
                  <Datum label="Of quota">{percent(period.consumedPct)}</Datum>
                </>
              )}
            </dl>
          </>
        )}
      </section>
    </>
  );
}

function HolderRecord({
  holder,
  all,
}: {
  readonly holder: HolderListItem;
  readonly all: readonly HolderListItem[];
}) {
  const path = ancestry(all, holder.id);

  return (
    <>
      <Datum label="Inside">
        {path.length === 0
          ? "Top level"
          : path.map((parent, index) => (
              <span key={parent.id}>
                {index === 0 ? null : " / "}
                <HolderLink id={parent.id} name={parent.name} />
              </span>
            ))}
      </Datum>
      <Datum label="Reference">{holder.externalRef ?? "—"}</Datum>
      <Datum label="Holder status">
        {holder.status === "active" ? "Active" : "Disabled"}
        {holder.isSystem ? " · system group" : ""}
      </Datum>
      <Datum label="Cards held">{holder.identityCount}</Datum>
      <Datum label="Holder since">{dateOnly(holder.createdAt, ZONE)}</Datum>
    </>
  );
}

function HolderLink({ id, name }: { readonly id: string; readonly name: string }) {
  return (
    <Link
      to={`/holders/${encodeURIComponent(id)}`}
      className="text-signal underline decoration-1 underline-offset-4 hover:no-underline"
    >
      {name}
    </Link>
  );
}

function tokenLabel(row: IdentityDetail): string {
  return row.tokenType === "vin" ? "VIN" : "RFID";
}
